"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Handshaker = void 0;
const dht_1 = require("@streamr/dht");
const utils_1 = require("@streamr/utils");
const NetworkRpc_1 = require("../../../generated/packages/trackerless-network/protos/NetworkRpc");
const NetworkRpc_client_1 = require("../../../generated/packages/trackerless-network/protos/NetworkRpc.client");
const ContentDeliveryRpcRemote_1 = require("../ContentDeliveryRpcRemote");
const HandshakeRpcLocal_1 = require("./HandshakeRpcLocal");
const HandshakeRpcRemote_1 = require("./HandshakeRpcRemote");
const logger = new utils_1.Logger(module);
const PARALLEL_HANDSHAKE_COUNT = 2;
class Handshaker {
    options;
    rpcLocal;
    constructor(options) {
        this.options = options;
        this.rpcLocal = new HandshakeRpcLocal_1.HandshakeRpcLocal({
            streamPartId: this.options.streamPartId,
            neighbors: this.options.neighbors,
            ongoingHandshakes: this.options.ongoingHandshakes,
            ongoingInterleaves: new Set(),
            maxNeighborCount: this.options.maxNeighborCount,
            handshakeWithInterleaving: (target, remoteNodeId) => this.handshakeWithInterleaving(target, remoteNodeId),
            createRpcRemote: (target) => this.createRpcRemote(target),
            createContentDeliveryRpcRemote: (target) => this.createContentDeliveryRpcRemote(target)
        });
        this.options.rpcCommunicator.registerRpcMethod(NetworkRpc_1.InterleaveRequest, NetworkRpc_1.InterleaveResponse, 'interleaveRequest', (req, context) => this.rpcLocal.interleaveRequest(req, context), { timeout: HandshakeRpcRemote_1.INTERLEAVE_REQUEST_TIMEOUT });
        this.options.rpcCommunicator.registerRpcMethod(NetworkRpc_1.StreamPartHandshakeRequest, NetworkRpc_1.StreamPartHandshakeResponse, 'handshake', (req, context) => this.rpcLocal.handshake(req, context));
    }
    async attemptHandshakesOnContacts(excludedIds) {
        // TODO use options option or named constant? or why the value 2?
        if (this.options.neighbors.size() + this.options.ongoingHandshakes.size < this.options.maxNeighborCount - 2) {
            logger.trace(`Attempting parallel handshakes with ${PARALLEL_HANDSHAKE_COUNT} targets`);
            return this.selectParallelTargetsAndHandshake(excludedIds);
        }
        else if (this.options.neighbors.size() + this.options.ongoingHandshakes.size < this.options.maxNeighborCount) {
            logger.trace(`Attempting handshake with new target`);
            return this.selectNewTargetAndHandshake(excludedIds);
        }
        return excludedIds;
    }
    async selectParallelTargetsAndHandshake(excludedIds) {
        const exclude = excludedIds.concat(this.options.neighbors.getIds());
        const neighbors = this.selectParallelTargets(exclude);
        neighbors.forEach((contact) => this.options.ongoingHandshakes.add((0, dht_1.toNodeId)(contact.getPeerDescriptor())));
        return this.doParallelHandshakes(neighbors, exclude);
    }
    selectParallelTargets(excludedIds) {
        const neighbors = new Map();
        // If the node has 0 neighbors find a node in the stream with a WS server to connect to for faster time to data.
        if (this.options.neighbors.size() === 0) {
            const wsNode = this.options.nearbyNodeView.getFirst([...excludedIds, ...Array.from(neighbors.keys())], true);
            if (wsNode) {
                const wsNodeId = (0, dht_1.toNodeId)(wsNode.getPeerDescriptor());
                excludedIds.push(wsNodeId);
                neighbors.set(wsNodeId, wsNode);
            }
        }
        // Add the closest left and then right contacts from the ring if possible.
        const left = this.options.leftNodeView.getFirst([...excludedIds, ...Array.from(neighbors.keys())]);
        const right = this.options.rightNodeView.getFirst([...excludedIds, ...Array.from(neighbors.keys())]);
        if (left) {
            neighbors.set((0, dht_1.toNodeId)(left.getPeerDescriptor()), left);
        }
        if (right) {
            neighbors.set((0, dht_1.toNodeId)(right.getPeerDescriptor()), right);
        }
        // If there is still room add the closest contact based on the kademlia metric
        if (neighbors.size < PARALLEL_HANDSHAKE_COUNT) {
            const first = this.options.nearbyNodeView.getFirst([...excludedIds, ...Array.from(neighbors.keys())]);
            if (first) {
                neighbors.set((0, dht_1.toNodeId)(first.getPeerDescriptor()), first);
            }
        }
        const getExcludedFromRandomView = () => [
            ...excludedIds,
            ...Array.from(neighbors.values()).map((neighbor) => (0, dht_1.toNodeId)(neighbor.getPeerDescriptor()))
        ];
        // If there is still room add a random contact until PARALLEL_HANDSHAKE_COUNT is reached
        while (neighbors.size < PARALLEL_HANDSHAKE_COUNT
            && this.options.randomNodeView.size(getExcludedFromRandomView()) > 0) {
            const random = this.options.randomNodeView.getRandom([...excludedIds, ...Array.from(neighbors.keys())]);
            if (random) {
                neighbors.set((0, dht_1.toNodeId)(random.getPeerDescriptor()), random);
            }
        }
        return Array.from(neighbors.values()).map((neighbor) => this.createRpcRemote(neighbor.getPeerDescriptor()));
    }
    async doParallelHandshakes(targets, excludedIds) {
        const results = await Promise.allSettled(Array.from(targets.values()).map(async (target, i) => {
            const otherNode = i === 0 ? targets[1] : targets[0];
            // TODO better check (currently this condition is always true)
            const otherNodeId = otherNode ? (0, dht_1.toNodeId)(otherNode.getPeerDescriptor()) : undefined;
            return this.handshakeWithTarget(target, otherNodeId);
        }));
        results.forEach((res, i) => {
            if (res.status !== 'fulfilled' || !res.value) {
                excludedIds.push((0, dht_1.toNodeId)(targets[i].getPeerDescriptor()));
            }
        });
        return excludedIds;
    }
    async selectNewTargetAndHandshake(excludedIds) {
        const exclude = excludedIds.concat(this.options.neighbors.getIds());
        const neighbor = this.options.leftNodeView.getFirst(exclude)
            ?? this.options.rightNodeView.getFirst(exclude)
            ?? this.options.nearbyNodeView.getFirst(exclude)
            ?? this.options.randomNodeView.getRandom(exclude);
        if (neighbor) {
            const accepted = await this.handshakeWithTarget(this.createRpcRemote(neighbor.getPeerDescriptor()));
            if (!accepted) {
                excludedIds.push((0, dht_1.toNodeId)(neighbor.getPeerDescriptor()));
            }
        }
        return excludedIds;
    }
    async handshakeWithTarget(neighbor, concurrentNodeId) {
        const targetNodeId = (0, dht_1.toNodeId)(neighbor.getPeerDescriptor());
        this.options.ongoingHandshakes.add(targetNodeId);
        const result = await neighbor.handshake(this.options.streamPartId, this.options.neighbors.getIds(), concurrentNodeId);
        if (result.accepted) {
            this.options.neighbors.add(this.createContentDeliveryRpcRemote(neighbor.getPeerDescriptor()));
        }
        if (result.interleaveTargetDescriptor) {
            await this.handshakeWithInterleaving(result.interleaveTargetDescriptor, targetNodeId);
        }
        this.options.ongoingHandshakes.delete(targetNodeId);
        return result.accepted;
    }
    async handshakeWithInterleaving(target, remoteNodeId) {
        const neighbor = this.createRpcRemote(target);
        const targetNodeId = (0, dht_1.toNodeId)(neighbor.getPeerDescriptor());
        this.options.ongoingHandshakes.add(targetNodeId);
        const result = await neighbor.handshake(this.options.streamPartId, this.options.neighbors.getIds(), undefined, remoteNodeId);
        if (result.accepted) {
            this.options.neighbors.add(this.createContentDeliveryRpcRemote(neighbor.getPeerDescriptor()));
        }
        this.options.ongoingHandshakes.delete(targetNodeId);
        return result.accepted;
    }
    createRpcRemote(targetPeerDescriptor) {
        return new HandshakeRpcRemote_1.HandshakeRpcRemote(this.options.localPeerDescriptor, targetPeerDescriptor, this.options.rpcCommunicator, NetworkRpc_client_1.HandshakeRpcClient, this.options.rpcRequestTimeout);
    }
    createContentDeliveryRpcRemote(targetPeerDescriptor) {
        return new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.options.localPeerDescriptor, targetPeerDescriptor, this.options.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient, this.options.rpcRequestTimeout);
    }
    getOngoingHandshakes() {
        return this.options.ongoingHandshakes;
    }
}
exports.Handshaker = Handshaker;
//# sourceMappingURL=Handshaker.js.map